import FlagIcon from "@material-ui/icons/Flag";
import Alert from "@material-ui/lab/Alert";
import { useAppContext } from "@qpr/client-api-base/context/AppCtxProvider";
import useCurrentUser from "@qpr/client-api-base/context/UserContext";
import useLink from "@qpr/client-api-base/hooks/useLink";
import UploadedFilesList from "@qpr/client-api-base/ui/components/UploadedFilesList";
import { MyTasksData } from "@qpr/client-api-workflow/types";
import flattenHierarchy from "backend/flattenHierarchy";
import useComments from "backend/hooks/comments/useComments";
import useElement from "backend/hooks/elements/useElement";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import useAttachments from "backend/hooks/files/useAttachments";
import useMyTasks from "backend/hooks/workflows/useMyTasks";
import useMyWorkflows from "backend/hooks/workflows/useMyWorkflows";
import useSubmitElementValues from "backend/hooks/workflows/useSubmitElementValues";
import useUpdateWorkflowState from "backend/hooks/workflows/useUpdateWorkflowState";
import useWorkflowInstance from "backend/hooks/workflows/useWorkflowInstance";
import useWorkflowLogs from "backend/hooks/workflows/useWorkflowLogs";
import { ICardElementWithChildren } from "backend/model/model";
import InitiativeApproveForm from "components/forms/valueinput/InitiativeApproveForm";
import InitiativeInputForm from "components/forms/valueinput/InitiativeInputForm";
import KPIApproveForm from "components/forms/valueinput/KPIApprove";
import KPIValueInput from "components/forms/valueinput/KPIInputForm";
import ElementLink from "components/link/ElementLink";
import WorkflowToolbar from "components/toolbars/WorkflowToolbar";
import WorkflowElementState from "components/workflow/WorkflowElementState";
import WorkflowLogs from "components/workflow/WorkflowLogs";
import WorkflowSteps from "components/workflow/WorkflowSteps";
import AnalysisCard from "design/components/cards/AnalysisCard/AnalysisCard";
import BudgetUtilizationCard from "design/components/cards/BudgetUtilizationCard/BudgetUtilizationCard";
import { YearEndTargetCard } from "design/components/cards/KPIPerformanceCard";
import SlippageCard from "design/components/cards/SlippageCard/SlippageCard";
import CardCheckedBox from "design/components/checkboxes/CardCheckedBox";
import PeriodChip from "design/components/chips/PeriodChip";
import MeasureTable from "design/components/tables/MeasureTable";
import DashboardTitle from "design/components/texts/DashboardTitle";
import ExecutiveDashboardTitle from "design/components/texts/ExecutiveDashboardTitle";
import PageContainer from "design/layouts/PageContainer";
import RowView from "design/layouts/RowView";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import Page from "design/pages/Page";
import { t } from "i18next";
import { useTheme } from "@material-ui/core/styles";
import React from "react";
import { If } from "react-if";
import { formatWFStatus, getWFStatusBadgeStyle } from "utils/utils";
/*

The allowed actions for the user are in format
["editvalues","editcomments","editdocuments","submit-enabled"]

*/
const WorkflowInput = () => {
  const theme = useTheme();
  const ctx = useAppContext();
  const link = useLink();
  const { data: task, isLoading: isTaskLoading } = useWorkflowInstance(
    ctx.elementId
  );
  const { data: logs, isLoading: isLogsLoading } = useWorkflowLogs(task?.id);
  const { data: instanceElement } = useElement(task?.meaId, task?.period);
  const { data: attachments } = useAttachments({
    elementId: task?.meaId,
    period: task?.period,
  });
  const saveValues = useSubmitElementValues();
  const { data: myTasks, isLoading: isMyTasksLoading } = useMyTasks(
    "coming",
    "wfinput"
  );
  const {
    data: myWorkflows,
    isLoading: isWorkflowsLoading,
    isRefetching: isPrevWf,
  } = useMyWorkflows();
  const selectedRow = React.useMemo(
    () =>
      myWorkflows
        ?.filter(
          (item) => item.workflowInstance?.wfInstanceId === ctx.elementId
        )
        .pop(),
    [myWorkflows, ctx.elementId]
  );
  const selectRow = React.useCallback(
    (item: ICardElementWithChildren) => {
      link({ meaId: item.workflowInstance?.wfInstanceId });
    },
    [ctx.elementId, ctx.page]
  );
  const { data: comments, isLoading: isLoadingComments } = useComments({
    id: selectedRow?.workflowInstance?.meaId || "",
    period: selectedRow?.workflowInstance?.period || "",
  });
  const user = useCurrentUser();
  const [firstComment] = comments ? comments : [];
  const [value, setValue] = React.useState(selectedRow?.ACT || "");
  const {
    mutate: changeWorkflowState,
    isError: hasWfStateChangeError,
    reset: resetWfChange,
  } = useUpdateWorkflowState();

  const { data: selectedRowChildren } = useHierarchy(
    selectedRow?.id,
    2,
    selectedRow?.workflowInstance?.period || "",
    selectedRow?.childobjects ? false : true // disable query if no childobjects
  );
  const milestones = React.useMemo(
    () =>
      flattenHierarchy(selectedRowChildren || []).filter(
        (item) => item.typename === "Milestone"
      ) || [],
    [selectedRowChildren, selectedRow]
  );

  const taskPerWf = React.useMemo(() => {
    const taskPerWf: Record<string, MyTasksData> = {};
    myTasks?.forEach((task) => {
      taskPerWf[task.workflowState.wfInstanceId] = task.workflowState;
    });

    return taskPerWf;
  }, [myTasks]);

  const initialSortedTaskList = React.useMemo(() => {
    if (!myWorkflows || !myTasks) return [];

    const newList = [...myWorkflows]
      .filter((item) => {
        return taskPerWf[item.workflowInstance?.wfInstanceId || ""];
      })
      .sort((a, b) => {
        const task1: MyTasksData =
          taskPerWf[a.workflowInstance?.wfInstanceId || ""];
        const task2: MyTasksData =
          taskPerWf[b.workflowInstance?.wfInstanceId || ""];
        if (task1.escalateInfo) return 3;
        if (task2.escalateInfo) return -3;

        if (task1.state.includes("rejected")) return 2;
        if (task2.state.includes("rejected")) return -2;
        if (
          task1.responsible === user.fullname &&
          task2.responsible !== user.fullname
        )
          return 1;
        if (
          task1.responsible !== user.fullname &&
          task2.responsible === user.fullname
        )
          return -1;

        return (
          (b.scorecardname || "").localeCompare(a.scorecardname || "") ||
          b.name.localeCompare(a.name)
        );
      });

    console.log("newList", newList);

    return newList.reverse();
  }, [myTasks, myWorkflows, user]);

  React.useEffect(() => {
    setValue(selectedRow?.ACT || "");
    resetWfChange();
  }, [selectedRow]);

  const hasAllowedActions =
    selectedRow?.workflowInstance?.allowedActions.length;

  const isDataEntryPhase = ["approvalrejected", "dataentry", "draft"].includes(
    selectedRow?.workflowInstance?.currentState || ""
  );

  const canReject =
    selectedRow?.workflowInstance?.allowedActions.includes("reject");
  const canApprove =
    selectedRow?.workflowInstance?.allowedActions.includes("approve");

  const isApprovalPhase =
    !isDataEntryPhase &&
    ["finalapprovalrejected", "approval", "finalapproval"].includes(
      selectedRow?.workflowInstance?.currentState || ""
    );

  return (
    <Page
      Toolbar={WorkflowToolbar}
      isLoading={
        isMyTasksLoading ||
        !myTasks ||
        isWorkflowsLoading ||
        saveValues.isLoading ||
        isPrevWf
      }
    >
      {!myWorkflows || myWorkflows?.length == 0 ? (
        <PageContainer>
          <ExecutiveDashboardTitle>
            At the moment there are no Workflows to manage.
          </ExecutiveDashboardTitle>
        </PageContainer>
      ) : (
        <SplitView>
          <div>
            <MeasureTable
              keyFn={(row) =>
                row?.workflowInstance?.wfInstanceId || row?.id || ""
              }
              selectedRow={selectedRow}
              items={initialSortedTaskList || []}
              columns={[
                {
                  title: t("workflowinput.table.Done").toString(),
                  onRowClick: selectRow,
                  sortFn: (a, b) =>
                    Number(a.workflowInstance?.done || 0) -
                    Number(b.workflowInstance?.done || 0),
                  value: (item) => {
                    return (
                      <div>
                        <CardCheckedBox
                          dx={0}
                          dy={-30}
                          type="blue"
                          isOpen={item.elem.workflowInstance?.done}
                        />
                      </div>
                    );
                  },
                },
                {
                  title: t("workflowinput.table.Name").toString(),
                  onRowClick: selectRow,
                  sortFn: (a, b) => a.name.localeCompare(b.name),
                  value: (item) => {
                    return (
                      <div>
                        <div>{item.elem.name}</div>
                        <div>
                          <i>{item.elem.scorecardname}</i>
                        </div>
                      </div>
                    );
                  },
                },
                {
                  title: t("workflowinput.table.Type").toString(),
                  onRowClick: selectRow,
                  value: (item) => {
                    return <div>{item.elem.typename}</div>;
                  },
                },
                {
                  title: t("workflowinput.table.State").toString(),
                  onRowClick: selectRow,
                  sortFn: (a, b) =>
                    formatWFStatus(a.workflowInstance?.currentState).localeCompare(
                      formatWFStatus(b.workflowInstance?.currentState)
                    ) ||
                    (a.workflowInstance?.currentState || "").localeCompare(
                      b.workflowInstance?.currentState || ""
                    ) ||
                    0,
                  value: (item) => {
                    const task =
                      taskPerWf[item.elem.workflowInstance?.wfInstanceId || ""];
                    const workflowState = item.elem.workflowInstance?.currentState;
                    const statusLabel = formatWFStatus(workflowState);
                    const badgeStyle = getWFStatusBadgeStyle(workflowState, theme);

                    return (
                      <div>
                        {task?.escalateInfo ? (
                          <div>
                            <div
                              style={{
                                ...badgeStyle,
                                display: "inline-flex",
                                alignItems: "center",
                                minHeight: "28px",
                                padding: "0 12px",
                                borderRadius: "999px",
                                fontSize: "12px",
                                fontWeight: 600,
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                boxShadow: "0 0 0 2px rgba(180, 35, 24, 0.08)",
                              }}
                            >
                              {statusLabel}
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              ...badgeStyle,
                              display: "inline-flex",
                              alignItems: "center",
                              minHeight: "28px",
                              padding: "0 12px",
                              borderRadius: "999px",
                              fontSize: "12px",
                              fontWeight: 600,
                              lineHeight: 1,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {statusLabel}
                          </div>
                        )}
                      </div>
                    );
                  },
                },
                {
                  title: t("workflowinput.table.Period").toString(),
                  onRowClick: selectRow,
                  sortFn: (a, b) =>
                    (a.workflowInstance?.period || "").localeCompare(
                      b.workflowInstance?.period || ""
                    ),
                  value: (item) => {
                    return <div>{item.elem.period?.name}</div>;
                  },
                },
                {
                  title: t("workflowinput.table.ACT").toString(),
                  onRowClick: selectRow,
                  value: (item) => {
                    return <div>{item.elem.ACT_prettyvalue}</div>;
                  },
                },
                {
                  title: t("workflowinput.table.Status").toString(),
                  onRowClick: selectRow,
                  value: (item) => {
                    const task =
                      taskPerWf[item.elem.workflowInstance?.wfInstanceId || ""];

                    return (
                      <div>
                        {task.delegateInfo ? (
                          <div
                            title={task.delegateInfo.message}
                            style={{
                              background: "#3377ff",
                              color: "white",
                              display: "inline-flex",
                              borderRadius: "20px",
                              margin: "3px",
                              padding: "5px",
                            }}
                          >
                            <FlagIcon style={{ fontSize: "16px" }} />
                          </div>
                        ) : null}
                      </div>
                    );
                  },
                },
              ]}
            />
          </div>
          <StackView padding="20px">
            {selectedRow?.workflowInstance?.delegates &&
            selectedRow?.workflowInstance?.delegates.length > 0 ? (
              <Alert>
                {selectedRow?.workflowInstance?.delegates
                  ?.map((item) => item.message)
                  .pop()}
                <div>
                  <i style={{ fontSize: "0.9em" }}>
                    {selectedRow?.workflowInstance?.delegates
                      ?.map((item) => item.senderFullname)
                      .pop()}
                  </i>
                </div>
              </Alert>
            ) : null}
            {!selectedRow && task ? (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <DashboardTitle>
                    {instanceElement?.name}{" "}
                    <ElementLink item={instanceElement} />{" "}
                  </DashboardTitle>
                  <div style={{ width: "140px" }}>
                    <PeriodChip period={task?.period || ""} icon="KPI" />
                  </div>
                </div>{" "}
                <Alert severity="success">Task was Completed</Alert>
              </>
            ) : null}
            {saveValues.error ? (
              <Alert severity="warning">{String(saveValues.error)}</Alert>
            ) : null}
            <If condition={!!selectedRow}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <DashboardTitle>
                  {selectedRow?.name} <ElementLink item={instanceElement} />
                </DashboardTitle>
                <div style={{ width: "140px" }}>
                  <PeriodChip period={task?.period || ""} icon="KPI" />
                </div>
              </div>
            </If>
            {hasWfStateChangeError ? (
              <Alert severity="error">
                Could not change the workflow state
              </Alert>
            ) : null}
            <WorkflowSteps
              workflowId={task?.id}
              showSteps={[
                {
                  title: "Data Input",
                  aliases: ["draft", "dataentry", "approvalrejected"],
                },
                {
                  title: "Approval",
                  aliases: ["approval", "finalapprovalrejected"],
                },
                { title: "Final Approval", aliases: ["finalapproval"] },
              ]}
            />
            <WorkflowElementState item={selectedRow} />
            {taskPerWf[selectedRow?.workflowInstance?.wfInstanceId || ""]
              ?.escalateInfo ? (
              <Alert severity="error">
                <div style={{ margin: "0px", fontWeight: "bold" }}>
                  Escalation
                </div>
                {
                  taskPerWf[selectedRow?.workflowInstance?.wfInstanceId || ""]
                    ?.escalateInfo?.message
                }{" "}
                -{" "}
                <i>
                  {
                    taskPerWf[selectedRow?.workflowInstance?.wfInstanceId || ""]
                      ?.escalateInfo?.senderFullname
                  }
                </i>
              </Alert>
            ) : null}
            <If condition={!!selectedRow && !isDataEntryPhase}>
              {selectedRow?.typename === "KPI" && !isApprovalPhase && (
                <RowView gap="20px">
                  <YearEndTargetCard
                    title={t("workflowinput.form.Actual")}
                    period={selectedRow?.workflowInstance?.period || ""}
                    value={selectedRow?.ACT}
                    unit={selectedRow?.ACT_unit}
                  />
                  <If condition={!!firstComment}>
                    <AnalysisCard
                      userData={{
                        name:
                          firstComment?.modifiedby || firstComment?.createdby,
                        subtitle: "Owner",
                      }}
                      date={new Date(
                        firstComment?.lastmodifieddate
                      ).toLocaleDateString()}
                      description={
                        firstComment
                          ? firstComment.description
                          : "No Comments yet"
                      }
                    >
                      <UploadedFilesList values={attachments} readOnly />
                    </AnalysisCard>
                  </If>
                </RowView>
              )}
              {selectedRow?.typename === "Initiative" && (
                <StackView>
                  <SplitView left="350px" right="320px" gap="20px">
                    <BudgetUtilizationCard
                      data={selectedRow}
                      display={{
                        colorKey: "rangecolor",
                        prettyvalue: "BUDGET_UTIL_prettyvalue",
                        value: "BUDGET_UTIL",
                      }}
                      series={[
                        {
                          title: "Total Budget",
                          prettyvalue: "BUDGET_prettyvalue",
                        },
                        {
                          title: "YTD Expenditure",
                          prettyvalue: "EXPENDITURE_prettyvalue",
                        },
                      ]}
                    />
                    <SlippageCard
                      data={selectedRow}
                      dateDisplay={{
                        title: "Estimated End Date",
                        value: "ACTUAL_END_DATE",
                      }}
                      display={{
                        title: "Slippage",
                        unit: "SLIPPAGE_unit",
                        value: "SLIPPAGE",
                        valuewithoutsymbol: "SLIPPAGE_valuewithoutsymbol",
                        unitConversionFunction: (str: string) => {
                          const slippageUnits: Record<string, string> = {
                            d: "Days",
                            w: "Weeks",
                            y: "Years",
                          };
                          return slippageUnits[str] || str;
                        },
                      }}
                    />
                  </SplitView>
                  <div>
                    {!isApprovalPhase ? (
                      <AnalysisCard
                        userData={{
                          name:
                            firstComment?.modifiedby || firstComment?.createdby,
                          subtitle: "Owner",
                        }}
                        description={
                          firstComment
                            ? firstComment.description
                            : "No Comments yet"
                        }
                      >
                        <UploadedFilesList values={attachments} readOnly />
                      </AnalysisCard>
                    ) : null}
                  </div>
                  <MeasureTable
                    key={selectedRow.id}
                    items={milestones}
                    columns={[
                      {
                        title: "Name",
                        value: (row) => <div>{row.elem.name}</div>,
                      },
                      {
                        title: "Period",
                        value: (row) => <div>{row.elem.period?.name}</div>,
                      },
                      {
                        title: "Estimated End Date",
                        value: (row) => (
                          <div>{row.elem.ACTUAL_END_DATE_prettyvalue}</div>
                        ),
                      },
                      {
                        title: "Completion Percentage",
                        value: (row) => (
                          <div>
                            {row.elem.COMPLETION_PERCENTAGE_prettyvalue}
                          </div>
                        ),
                      },
                    ]}
                  ></MeasureTable>
                </StackView>
              )}
            </If>
            <If
              condition={hasAllowedActions && isApprovalPhase && !!selectedRow}
            >
              {selectedRow?.typename === "Initiative" && (
                <InitiativeApproveForm
                  key={selectedRow?.workflowInstance?.wfInstanceId}
                  selectedRow={selectedRow!}
                  comments={comments}
                  canApprove={canApprove}
                  canReject={canReject}
                  onSubmit={(values) => {
                    saveValues.mutate(values);
                  }}
                />
              )}
              {selectedRow?.typename === "KPI" && (
                <KPIApproveForm
                  key={selectedRow?.workflowInstance?.wfInstanceId}
                  selectedRow={selectedRow!}
                  comments={comments}
                  canApprove={canApprove}
                  canReject={canReject}
                  onSubmit={(values) => {
                    saveValues.mutate(values);
                  }}
                />
              )}
            </If>
            {selectedRow?.workflowInstance?.allowedActions.includes(
              "editvalues"
            ) &&
            isDataEntryPhase &&
            hasAllowedActions &&
            !!selectedRow ? (
              <>
                <If condition={selectedRow?.typename === "KPI"}>
                  <KPIValueInput
                    item={selectedRow!}
                    onSave={(values) => {
                      console.log("Workflow Input Save KPI (draft)", values);
                      saveValues.mutate({
                        fields: values,
                        transition: "SAVE",
                      });
                    }}
                    onSubmit={(values) => {
                      console.log("Workflow Input Submit KPI", values);

                      saveValues.mutate({
                        fields: values,
                        transition: "SUBMIT",
                      });
                    }}
                  />
                </If>
                <If condition={selectedRow?.typename === "Initiative"}>
                  <InitiativeInputForm
                    item={selectedRow!}
                    onSave={(values) => {
                      console.log("Workflow Input Save Initiative (draft)", values);                       
                      saveValues.mutate({
                        fields: values,
                        transition: "SAVE",
                      });
                    }}
                    onSubmit={(values) => {
                      console.log("Workflow Input Submit Initiatives", values);

                      saveValues.mutate({
                        fields: values,
                        transition: "SUBMIT",
                      });
                    }}
                  />
                </If>
              </>
            ) : null}
            <WorkflowLogs
              workflowId={selectedRow?.workflowInstance?.wfInstanceId}
            />
          </StackView>
        </SplitView>
      )}
    </Page>
  );
};

export default WorkflowInput;
